import { createSlice } from "@reduxjs/toolkit";
import { resetAll } from "./baseSlice";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";

const initialState = {
  data: [],
  record: null,
  requesting: false,
  error: null,
  action: null,
  query: {
    pageNo: 1,
    pageSize: 50,
    sortBy: "asc",
    sortColumn: "firstName",
    namePattern: "",
  },
  filters: [],
  summary: {},
};

const customIntl = intl();

const usersSlice = createSlice({
  name: "users",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    listRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "list";
      state.record = null;
    },
    listSuccess: (state, action) => {
      state.data = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    listFail: (state, action) => {
      state.data = [];
      state.requesting = false;
      state.error = action.payload;
    },
    adminListRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "list";
      state.record = null;
    },
    createRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "create";
    },
    bulkCreateRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "bulk-create";
    },
    createSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          {
            id:
              state.action === "create"
                ? "user_create_success"
                : "user_bulk_create_success",
          },
          {
            count: action.payload.count || 0,
          }
        )
      );
    },
    createFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(
          customIntl.formatMessage({
            id:
              action.payload?.errorCode?.toLowerCase() ||
              "something_else_wrong",
          })
        );
      }
    },
    performActionRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = action.payload.action;
    },
    performActionSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;

      let message = "action_success";
      if (action.payload?.count > 1) {
        message = "action_success_multiple";
      }

      toast.success(
        customIntl.formatMessage(
          { id: message },
          {
            action: customIntl.formatMessage({
              id: `${state.action}_performed`,
            }),
            object: customIntl.formatMessage({ id: "user" }),
            count: action.payload.count || 0,
          }
        )
      );
    },
    performActionFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    userDetailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "user_details";
    },
    userDetailsSuccess: (state, action) => {
      state.record = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    userDetailsFail: (state, action) => {
      state.record = null;
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    updateUserDetailRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update";
    },
    updateUserDetailSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "action_success" },
          {
            action: customIntl.formatMessage({ id: "update_performed" }),
            object: customIntl.formatMessage({ id: "user" }),
          }
        )
      );
    },
    updateUserDetailFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    setQuery: (state, action) => {
      state.query = {
        ...state.query,
        ...action.payload,
      };
    },
    summaryRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "summary";
    },
    summarySuccess: (state, action) => {
      state.summary = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    summaryFail: (state, action) => {
      state.summary = {};
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    downloadTemplateRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "download_template";
    },
    downloadTemplateSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
    },
    downloadTemplateFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    uploadUsersRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "upload-users";
    },
    uploadUsersSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      toast.success(
        customIntl.formatMessage(
          { id: "upload_success" },
          { ...action.payload.response }
        )
      );
    },
    uploadUsersFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      toast.error(
        customIntl.formatMessage({
          id: action.payload?.errorCode?.toLowerCase() || "upload_users_error",
        })
      );
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    userExportRequest: (state, action) => {},
    userExportSuccess: (state, action) => {},
    userExportFail: (state, action) => {
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    userPendingAssignmentRequest: (state, action) => {
      state.requesting = true;
      state.action = "pending-assignments";
      state.record = {
        ...state.record,
        pendingAssignments: [],
      };
    },
    userPendingAssignmentSuccess: (state, action) => {
      state.requesting = false;
      state.record = {
        ...state.record,
        pendingAssignments: action.payload.response,
      };
    },
    userPendingAssignmentFail: (state, action) => {
      state.requesting = false;
      state.record = {
        ...state.record,
        pendingAssignments: [],
      };
    },
    markAssignmentAsCompletedRequest: (state, action) => {
      state.requesting = true;
      state.action = "mark-as-completed";
    },
    markAssignmentAsCompletedSuccess: (state, action) => {
      state.requesting = false;
      toast.success(
        customIntl.formatMessage({ id: "mark_assignment_completed" })
      );
    },
    markAssignmentAsCompletedFail: (state, action) => {
      state.requesting = false;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
  },
});

export const {
  bulkCreateRequest,
  createFail,
  createRequest,
  createSuccess,
  downloadTemplateFail,
  downloadTemplateRequest,
  downloadTemplateSuccess,
  listFail,
  listRequest,
  listSuccess,
  performActionFail,
  performActionRequest,
  performActionSuccess,
  setFilters,
  setQuery,
  summaryFail,
  summaryRequest,
  summarySuccess,
  updateUserDetailFail,
  updateUserDetailRequest,
  updateUserDetailSuccess,
  uploadUsersFail,
  uploadUsersRequest,
  uploadUsersSuccess,
  userDetailsFail,
  userDetailsRequest,
  userDetailsSuccess,
  adminListRequest,
  userExportFail,
  userExportRequest,
  userExportSuccess,
  userPendingAssignmentRequest,
  userPendingAssignmentFail,
  userPendingAssignmentSuccess,
  markAssignmentAsCompletedFail,
  markAssignmentAsCompletedRequest,
  markAssignmentAsCompletedSuccess,
} = usersSlice.actions;
export default usersSlice.reducer;
